import React, { useRef, useState } from "react";
import { Camera, Loader2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { uploadPhoto, type CapturedPhoto, type WatermarkContext } from "./capture";
import type { GpsFix } from "@/features/gps/useGps";

// Drop-in button that handles the full photo-capture pipeline. Callers give
// it the watermark context (project/site/pack) and a GPS fix; it returns
// a CapturedPhoto via onCaptured when the upload (or offline queue) completes.

interface Props {
  ctx: WatermarkContext;
  gps: GpsFix | null;
  onCaptured: (photo: CapturedPhoto) => void;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

export function PhotoCaptureButton({ ctx, gps, onCaptured, label = "Photo", multiple = false, disabled, size = "md" }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<{ status: "idle" | "processing" | "done" | "error"; msg?: string; count?: number }>({ status: "idle" });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setState({ status: "processing", count: files.length });

    try {
      const results: CapturedPhoto[] = [];
      for (let i = 0; i < files.length; i++) {
        setState({ status: "processing", count: files.length, msg: `${i + 1} of ${files.length}` });
        const res = await uploadPhoto(files[i], ctx, gps);
        onCaptured(res);
        results.push(res);
      }
      const offlineCount = results.filter(r => r.queued_offline).length;
      setState({
        status: "done",
        msg: offlineCount > 0 ? `${offlineCount} queued offline` : `${results.length} uploaded`,
      });
      setTimeout(() => setState({ status: "idle" }), 2500);
    } catch (err: any) {
      setState({ status: "error", msg: err.message || String(err) });
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const sizes = { sm: "px-3 py-2 text-xs", md: "px-4 py-2.5 text-[13px]", lg: "px-5 py-3 text-sm" };
  const iconSize = { sm: 12, md: 14, lg: 16 }[size];

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
        hidden
        disabled={disabled || state.status === "processing"}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || state.status === "processing"}
        className={`inline-flex items-center gap-2 rounded font-medium transition-colors ${sizes[size]} ${
          state.status === "error"
            ? "bg-danger/12 border border-danger/30 text-danger"
            : state.status === "done"
              ? "bg-ok/12 border border-ok/30 text-ok"
              : state.status === "processing"
                ? "bg-accent/20 text-accent"
                : "bg-accent text-black hover:bg-accent-hover"
        } disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {state.status === "processing" ? <Loader2 size={iconSize} className="animate-spin" />
          : state.status === "done" ? <CheckCircle2 size={iconSize} />
          : state.status === "error" ? <AlertTriangle size={iconSize} />
          : <Camera size={iconSize} />}
        {state.status === "processing" ? (state.msg ?? "Processing…")
          : state.status === "done" ? (state.msg ?? "Done")
          : state.status === "error" ? "Try again"
          : label}
      </button>
    </>
  );
}
